import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Percent, Landmark, TrendingUp, ArrowLeft, Wallet, Scale, TrendingDown, FileDown } from "lucide-react";
import api, { fmtEur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { exportReportFiscale } from "../lib/exporter";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const num = (v) => (v === "" || v == null ? 0 : Number(String(v).replace(",", ".")) || 0);
const dedPerc = (r) => (r.deducibilita_perc == null || r.deducibilita_perc === "") ? 100 : num(r.deducibilita_perc);

export default function Tasse() {
  const { selectedStructure, structures = [], companyLogo } = useAuth();
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [aliquota, setAliquota] = useState("");
  const [detailMk, setDetailMk] = useState(null);

  const { data: cfg } = useQuery({ queryKey: ["tasse-config"], queryFn: async () => (await api.get("/tasse/config")).data });
  useEffect(() => { if (cfg && aliquota === "") setAliquota(String(cfg.aliquota ?? "")); }, [cfg]); // eslint-disable-line

  const { data: mensile = [] } = useQuery({
    queryKey: ["controllo-mensile", selectedStructure],
    queryFn: async () => (await api.get("/controllo/mensile", { params: { structure_id: selectedStructure } })).data,
  });
  const { data: mutui = [] } = useQuery({ queryKey: ["mutui-tasse"], queryFn: async () => (await api.get("/mutui")).data });
  const { data: cespiti = [] } = useQuery({ queryKey: ["ammortamenti", selectedStructure], queryFn: async () => (await api.get("/ammortamenti-cespiti", { params: { structure_id: selectedStructure } })).data });
  const { data: speseAll = [] } = useQuery({ queryKey: ["spese-tasse", selectedStructure], queryFn: async () => (await api.get("/spese", { params: { structure_id: selectedStructure } })).data });

  const saveAliquota = async (v) => {
    try { await api.put("/tasse/config", { aliquota: num(v) }); toast.success("Aliquota salvata"); }
    catch { toast.error("Errore salvataggio aliquota"); }
  };

  const al = num(aliquota);
  // Utile (risultato) per mese, sommato su tutte le strutture
  const utileByMese = {};
  mensile.forEach((r) => { if ((r.mese || "").slice(0, 4) === String(anno)) utileByMese[r.mese] = (utileByMese[r.mese] || 0) + (r.risultato || 0); });

  // Interessi e quota capitale mutui del mese (dal piano di ogni mutuo)
  const mutuoMese = (mk) => {
    let interessi = 0, capitale = 0;
    mutui.forEach((m) => (m.piano || []).forEach((r) => { if ((r.data || "").slice(0, 7) === mk) { interessi += num(r.interessi); capitale += num(r.capitale); } }));
    return { interessi, capitale };
  };
  // indice mese di ammortamento
  const idxMese = (start, mk) => {
    const st = (start || "").slice(0, 7); if (!st) return null;
    const [sy, sm] = st.split("-").map(Number); const [cy, cm] = mk.split("-").map(Number);
    return (cy - sy) * 12 + (cm - sm);
  };
  // Quota ammortamento DEDUCIBILE del mese (cespiti + spese capitalizzate)
  const ammDedMese = (mk) => {
    let q = 0;
    cespiti.forEach((c) => {
      const idx = idxMese(c.data_inizio, mk); if (idx == null) return;
      const dur = num(c.aliquota) > 0 ? Math.round((100 / num(c.aliquota)) * 12) : 0;
      if (idx >= 0 && (dur === 0 || idx < dur)) {
        const quota = (num(c.valore) * (num(c.aliquota) / 100)) / 12;
        q += quota * ((c.deducibilita == null ? 100 : num(c.deducibilita)) / 100);
      }
    });
    speseAll.forEach((s) => {
      if (!s.capitalizza) return;
      const idx = idxMese(s.data_documento, mk); if (idx == null) return;
      const dur = num(s.aliquota_amm) > 0 ? Math.round((100 / num(s.aliquota_amm)) * 12) : 0;
      if (idx >= 0 && (dur === 0 || idx < dur)) {
        const quota = (num(s.totale) * (num(s.aliquota_amm) / 100)) / 12;
        q += quota * (dedPerc(s) / 100);
      }
    });
    return q;
  };
  // Rettifiche fiscali del mese: spese capitalizzate (tolte dai costi) + quota indeducibile spese
  const rettificheMese = (mk) => {
    let cap = 0, indeduc = 0;
    speseAll.forEach((s) => {
      if ((s.data_documento || "").slice(0, 7) !== mk) return;
      if (s.capitalizza) cap += num(s.totale);
      else if (dedPerc(s) < 100) indeduc += num(s.totale) * (1 - dedPerc(s) / 100);
    });
    return { cap, indeduc };
  };

  const righe = MESI.map((label, m) => {
    const mk = `${anno}-${String(m + 1).padStart(2, "0")}`;
    const utileRC = utileByMese[mk];
    const { interessi, capitale } = mutuoMese(mk);
    const amm = ammDedMese(mk);
    const { cap, indeduc } = rettificheMese(mk);
    const rettifiche = cap + indeduc;
    const haDati = utileRC !== undefined || interessi > 0 || amm > 0 || capitale > 0 || rettifiche > 0;
    const anteImposte = (utileRC || 0) + cap + indeduc - interessi - amm;
    const tasse = Math.max(0, anteImposte) * al / 100;
    const utileNetto = anteImposte - tasse;
    const cassa = (utileRC || 0) - interessi - capitale - tasse;
    return { label, mk, m, utileRC: utileRC || 0, haDati, interessi, capitale, amm, cap, indeduc, rettifiche, anteImposte, tasse, utileNetto, cassa };
  });
  const mesiConDati = righe.filter((r) => r.haDati);
  const sum = (f) => mesiConDati.reduce((a, r) => a + f(r), 0);
  const totTasse = sum((r) => r.tasse);
  const totUtileNetto = sum((r) => r.utileNetto);
  const totCassa = sum((r) => r.cassa);
  const mediaMensile = mesiConDati.length ? totTasse / mesiConDati.length : 0;
  const totRC = sum((r) => r.utileRC);
  const totCap = sum((r) => r.cap);
  const totIndeduc = sum((r) => r.indeduc);
  const totRettifiche = sum((r) => r.rettifiche);
  const totInteressi = sum((r) => r.interessi);
  const totCapitale = sum((r) => r.capitale);
  const totAmm = sum((r) => r.amm);
  const totAnteImposte = sum((r) => r.anteImposte);
  const structuraLabel = selectedStructure === "all" ? "Tutte le strutture" : (structures.find((s) => s.id === selectedStructure)?.nome || "Struttura");
  const scaricaReport = () => {
    try {
      exportReportFiscale({
        anno, subtitle: structuraLabel, aliquota: al, righe, logo: companyLogo,
        totals: { rc: totRC, cap: totCap, indeduc: totIndeduc, rettifiche: totRettifiche, interessi: totInteressi, capitale: totCapitale, amm: totAmm, anteImposte: totAnteImposte, tasse: totTasse, utileNetto: totUtileNetto, cassa: totCassa },
      });
      toast.success("Report fiscale generato");
    } catch { toast.error("Errore nella generazione del report"); }
  };

  // ---- Vista pagina intera: dettaglio del mese ----
  if (detailMk) {
    const dr = righe.find((r) => r.mk === detailMk);
    if (!dr) { setDetailMk(null); return null; }
    const mutuiMese = mutui.map((mm) => {
      let i = 0, c = 0; (mm.piano || []).forEach((rr) => { if ((rr.data || "").slice(0, 7) === detailMk) { i += num(rr.interessi); c += num(rr.capitale); } });
      return { nome: mm.descrizione || "Mutuo", banca: mm.banca, i, c };
    }).filter((x) => x.i > 0 || x.c > 0);
    const cespitiMese = cespiti.map((cc) => {
      const idx = idxMese(cc.data_inizio, detailMk); if (idx == null) return null;
      const dur = num(cc.aliquota) > 0 ? Math.round((100 / num(cc.aliquota)) * 12) : 0;
      if (idx < 0 || (dur !== 0 && idx >= dur)) return null;
      const quota = (num(cc.valore) * (num(cc.aliquota) / 100)) / 12;
      return { nome: cc.descrizione || "Cespite", q: quota * ((cc.deducibilita == null ? 100 : num(cc.deducibilita)) / 100) };
    }).filter(Boolean);
    const speseAmmMese = speseAll.map((s) => {
      if (!s.capitalizza) return null;
      const idx = idxMese(s.data_documento, detailMk); if (idx == null) return null;
      const dur = num(s.aliquota_amm) > 0 ? Math.round((100 / num(s.aliquota_amm)) * 12) : 0;
      if (idx < 0 || (dur !== 0 && idx >= dur)) return null;
      const quota = (num(s.totale) * (num(s.aliquota_amm) / 100)) / 12;
      return { nome: (s.descrizione || s.fornitore || "Bene") + " (da spese)", q: quota * (dedPerc(s) / 100) };
    }).filter(Boolean);
    const ammBreak = [...cespitiMese, ...speseAmmMese];
    const capMese = speseAll.filter((s) => s.capitalizza && (s.data_documento || "").slice(0, 7) === detailMk).map((s) => ({ nome: s.descrizione || s.fornitore || "Bene", val: num(s.totale) }));
    const indeducMese = speseAll.filter((s) => !s.capitalizza && dedPerc(s) < 100 && (s.data_documento || "").slice(0, 7) === detailMk).map((s) => ({ nome: s.descrizione || s.fornitore || "Spesa", ded: dedPerc(s), val: num(s.totale) * (1 - dedPerc(s) / 100) }));

    const Step = ({ n, op, label, val, sub, color, children }) => (
      <div className="flex items-start gap-3 py-3 border-b last:border-0">
        <div className="h-7 w-7 shrink-0 rounded-full bg-secondary grid place-items-center text-xs font-bold text-muted-foreground mt-0.5">{n}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-semibold">{op && <span className="text-muted-foreground font-normal mr-1">{op}</span>}{label}</span>
            <span className={`tabnum font-bold text-base ${color || ""}`}>{val}</span>
          </div>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          {children}
        </div>
      </div>
    );
    const SubRows = ({ items }) => items.length > 0 && (
      <div className="mt-1.5 space-y-0.5">
        {items.map((x, k) => <div key={k} className="flex justify-between text-xs text-muted-foreground pl-1"><span>{x.label}</span><span className="tabnum">{x.val}</span></div>)}
      </div>
    );

    let step = 0;
    return (
      <div className="space-y-5 max-w-3xl" data-testid="tasse-detail-page">
        <button onClick={() => setDetailMk(null)} className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors" data-testid="tasse-detail-back">
          <ArrowLeft size={16} /> Torna alle Tasse
        </button>

        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Landmark size={22} className="text-rose-600" /> {dr.label} {anno}</h1>
          <p className="text-sm text-muted-foreground">Dettaglio del calcolo di tasse e cassa effettiva del mese.</p>
        </div>

        {/* Box riepilogo */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="gr-card p-4 border-l-4" style={{ borderColor: "#e11d48" }} data-testid="tasse-detail-box-ante">
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Utile ante imposte</div>
            <div className={`text-xl font-head font-bold mt-1 ${dr.anteImposte < 0 ? "text-rose-700" : "text-foreground"}`}>{fmtEur(dr.anteImposte)}</div>
          </div>
          <div className="gr-card p-4 border-l-4" style={{ borderColor: "#c2660c" }} data-testid="tasse-detail-box-tasse">
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Tasse ({al}%)</div>
            <div className="text-xl font-head font-bold text-amber-700 mt-1">{fmtEur(dr.tasse)}</div>
          </div>
          <div className="gr-card p-4 border-l-4" style={{ borderColor: "#1f7a5c" }} data-testid="tasse-detail-box-netto">
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><TrendingUp size={12} /> Utile netto</div>
            <div className={`text-xl font-head font-bold mt-1 ${dr.utileNetto < 0 ? "text-rose-700" : "text-primary"}`}>{fmtEur(dr.utileNetto)}</div>
          </div>
          <div className="gr-card p-4 border-l-4" style={{ borderColor: "#2563eb" }} data-testid="tasse-detail-box-cassa">
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Wallet size={12} /> Cassa effettiva</div>
            <div className={`text-xl font-head font-bold mt-1 ${dr.cassa < 0 ? "text-rose-700" : "text-blue-700"}`}>{fmtEur(dr.cassa)}</div>
          </div>
        </div>

        {/* Passo 1: come si arriva all'utile netto e alle tasse */}
        <div className="gr-card p-5" data-testid="tasse-detail-calc-imposte">
          <h2 className="font-head font-bold text-lg mb-1">Come si calcolano le tasse</h2>
          <p className="text-sm text-muted-foreground mb-3">Si parte dal risultato di gestione (Ricavi − Costi), si applicano le rettifiche fiscali, si tolgono interessi e ammortamenti, poi si applica l'aliquota.</p>
          <div className="text-sm">
            <Step n={++step} label="Ricavi − Costi (risultato di gestione)" val={fmtEur(dr.utileRC)} color={dr.utileRC < 0 ? "text-rose-600" : ""} sub="Dal modulo «Ricavi − Costi» (controllo mensile), a costi pieni." />
            {dr.cap > 0 && (
              <Step n={++step} op="+" label="Spese capitalizzate (tolte dai costi)" val={`+ ${fmtEur(dr.cap)}`} color="text-violet-700" sub="I beni da ammortizzare non sono un costo dell'anno: si ri-aggiungono qui e pesano solo come quota di ammortamento.">
                <SubRows items={capMese.map((x) => ({ label: x.nome, val: `+ ${fmtEur(x.val)}` }))} />
              </Step>
            )}
            {dr.indeduc > 0 && (
              <Step n={++step} op="+" label="Quota indeducibile spese" val={`+ ${fmtEur(dr.indeduc)}`} color="text-violet-700" sub="La parte non deducibile delle spese a deducibilità ridotta aumenta l'imponibile.">
                <SubRows items={indeducMese.map((x) => ({ label: `${x.nome} · deduc. ${x.ded}%`, val: `+ ${fmtEur(x.val)}` }))} />
              </Step>
            )}
            <Step n={++step} op="−" label="Interessi mutui" val={dr.interessi ? `− ${fmtEur(dr.interessi)}` : "0,00 €"} sub="Solo la quota interessi delle rate del mese (è un costo).">
              <SubRows items={mutuiMese.map((x) => ({ label: `${x.nome}${x.banca ? ` · ${x.banca}` : ""}`, val: `int. ${fmtEur(x.i)}` }))} />
            </Step>
            <Step n={++step} op="−" label="Ammortamenti (quota deducibile)" val={dr.amm ? `− ${fmtEur(dr.amm)}` : "0,00 €"} sub="Quota mensile deducibile di cespiti e beni capitalizzati.">
              <SubRows items={ammBreak.map((x) => ({ label: x.nome, val: fmtEur(x.q) }))} />
            </Step>
            <Step n={++step} op="=" label="Utile ante imposte" val={fmtEur(dr.anteImposte)} color={dr.anteImposte < 0 ? "text-rose-600" : "text-foreground"} sub="Base su cui si calcolano le tasse." />
            <Step n={++step} op="×" label={`Tasse (aliquota ${al}%)`} val={dr.tasse ? `− ${fmtEur(dr.tasse)}` : "0,00 €"} color="text-rose-700" sub={dr.anteImposte <= 0 ? "Utile ante imposte ≤ 0 → nessuna tassa dovuta." : `${al}% di ${fmtEur(dr.anteImposte)}.`} />
            <Step n={++step} op="=" label="Utile netto" val={fmtEur(dr.utileNetto)} color={dr.utileNetto < 0 ? "text-rose-600" : "text-primary"} sub="Utile ante imposte − tasse." />
          </div>
        </div>

        {/* Passo 2: cassa effettiva */}
        <div className="gr-card p-5" data-testid="tasse-detail-calc-cassa">
          <h2 className="font-head font-bold text-lg mb-1">Come si calcola la cassa effettiva</h2>
          <p className="text-sm text-muted-foreground mb-3">La cassa effettiva è il denaro che entra o esce davvero: si parte dal risultato di gestione (già a costi pieni, incluse le spese per beni) e si tolgono interessi, quota capitale dei mutui e le tasse. Gli ammortamenti non c'entrano: non sono uscite di cassa.</p>
          <div className="text-sm">
            <Step n="1" label="Ricavi − Costi (cassa operativa)" val={fmtEur(dr.utileRC)} color={dr.utileRC < 0 ? "text-rose-600" : ""} sub="Uscite reali del mese comprese (anche i beni acquistati)." />
            <Step n="2" op="−" label="Interessi mutui" val={dr.interessi ? `− ${fmtEur(dr.interessi)}` : "0,00 €"} />
            <Step n="3" op="−" label="Quota capitale mutui" val={dr.capitale ? `− ${fmtEur(dr.capitale)}` : "0,00 €"} sub="Rimborso del debito: esce dalla cassa ma non è un costo.">
              <SubRows items={mutuiMese.filter((x) => x.c > 0).map((x) => ({ label: `${x.nome}${x.banca ? ` · ${x.banca}` : ""}`, val: `cap. ${fmtEur(x.c)}` }))} />
            </Step>
            <Step n="4" op="−" label="Tasse" val={dr.tasse ? `− ${fmtEur(dr.tasse)}` : "0,00 €"} color="text-rose-700" />
            <Step n="5" op="=" label="Cassa effettiva" val={fmtEur(dr.cassa)} color={dr.cassa < 0 ? "text-rose-600" : "text-blue-700"} sub="Denaro che effettivamente resta o manca nel mese." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="tasse-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Landmark size={22} className="text-rose-600" /> Risultati & Tasse</h1>
          <p className="text-sm text-muted-foreground">Stima delle tasse per mese partendo dai Ricavi − Costi, con le rettifiche fiscali. Clicca un mese per il dettaglio.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="gr-card px-3 py-2 flex items-center gap-2" data-testid="tasse-aliquota-box">
            <Percent size={16} className="text-rose-600" />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground leading-none">Aliquota</div>
              <div className="flex items-center gap-1">
                <Input value={aliquota} onChange={(e) => setAliquota(e.target.value.replace(/[^\d.,]/g, ""))} onBlur={(e) => saveAliquota(e.target.value)}
                  placeholder="0" className="h-8 w-16 text-right tabnum font-bold border-0 px-1 focus-visible:ring-1" data-testid="tasse-aliquota-input" />
                <span className="font-bold text-muted-foreground">%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button className="gr-card h-10 w-10 grid place-items-center hover:border-primary" onClick={() => setAnno((a) => a - 1)} data-testid="tasse-year-prev"><ChevronLeft size={18} /></button>
          <span className="font-head font-bold text-xl tabnum" data-testid="tasse-year">{anno}</span>
          <button className="gr-card h-10 w-10 grid place-items-center hover:border-primary" onClick={() => setAnno((a) => a + 1)} data-testid="tasse-year-next"><ChevronRight size={18} /></button>
        </div>
        <Button variant="outline" onClick={scaricaReport} disabled={!mesiConDati.length} data-testid="tasse-report-btn">
          <FileDown size={15} className="mr-1.5" /> Report commercialista (PDF)
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="gr-card p-5 border-l-4" style={{ borderColor: "#e11d48" }} data-testid="tasse-tot-anno">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Totale tasse da inizio anno</div>
          <div className="text-2xl font-head font-bold text-rose-700 mt-1">{fmtEur(totTasse)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Aliquota {al}% · {mesiConDati.length} mesi</div>
        </div>
        <div className="gr-card p-5 border-l-4" style={{ borderColor: "#c2660c" }} data-testid="tasse-media">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Media mensile tasse</div>
          <div className="text-2xl font-head font-bold text-amber-700 mt-1">{fmtEur(mediaMensile)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Sui mesi con dati</div>
        </div>
        <div className="gr-card p-5 border-l-4" style={{ borderColor: "#1f7a5c" }} data-testid="tasse-utile-netto">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><TrendingUp size={13} /> Utile netto anno</div>
          <div className={`text-2xl font-head font-bold mt-1 ${totUtileNetto < 0 ? "text-rose-700" : "text-primary"}`}>{fmtEur(totUtileNetto)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Dopo rettifiche, interessi, ammortamenti e tasse</div>
        </div>
        <div className="gr-card p-5 border-l-4" style={{ borderColor: "#2563eb" }} data-testid="tasse-cassa">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Cassa effettiva anno</div>
          <div className={`text-2xl font-head font-bold mt-1 ${totCassa < 0 ? "text-rose-700" : "text-blue-700"}`}>{fmtEur(totCassa)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Cassa reale dopo mutui e tasse</div>
        </div>
      </div>

      <div className="gr-card p-5" data-testid="tasse-riepilogo-fiscale">
        <div className="flex items-center gap-2 mb-3">
          <Scale size={16} className="text-violet-700" />
          <h2 className="font-head font-bold">Riepilogo fiscale {anno}</h2>
          <span className="text-xs text-muted-foreground">— le rettifiche applicate ai Ricavi − Costi</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-4" data-testid="tasse-tot-capitalizzato">
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><TrendingDown size={13} className="text-violet-700" /> Capitalizzato (beni)</div>
            <div className="text-xl font-head font-bold text-violet-700 mt-1">{fmtEur(totCap)}</div>
            <div className="text-[11px] text-muted-foreground mt-1">Spese-beni tolte dai costi e ammortizzate</div>
          </div>
          <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-4" data-testid="tasse-tot-indeducibile">
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Scale size={13} className="text-violet-700" /> Quota indeducibile</div>
            <div className="text-xl font-head font-bold text-violet-700 mt-1">{fmtEur(totIndeduc)}</div>
            <div className="text-[11px] text-muted-foreground mt-1">Parte non deducibile delle spese</div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4" data-testid="tasse-tot-ammortamenti">
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><TrendingDown size={13} className="text-amber-600" /> Ammortamenti (deducibili)</div>
            <div className="text-xl font-head font-bold text-amber-700 mt-1">{fmtEur(totAmm)}</div>
            <div className="text-[11px] text-muted-foreground mt-1">Cespiti + beni capitalizzati</div>
          </div>
        </div>
      </div>

      <div className="gr-card p-5" data-testid="tasse-spiegazione">
        <div className="flex items-center gap-2 mb-2"><Scale size={16} className="text-rose-600" /><h2 className="font-head font-bold">Cosa abbiamo considerato nel calcolo</h2></div>
        <p className="text-sm text-muted-foreground mb-3">Per ogni mese partiamo dal risultato di gestione e applichiamo le rettifiche fiscali, gli interessi e gli ammortamenti. Ecco le fonti e le formule usate:</p>
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg border p-3">
            <div className="font-semibold flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary" /> Ricavi − Costi (gestionale)</div>
            <p className="text-xs text-muted-foreground mt-1">Dal modulo «Ricavi − Costi» (controllo mensile): ricavi incassati − tutte le spese − costo del personale, a costi pieni.</p>
          </div>
          <div className="rounded-lg border p-3">
            <div className="font-semibold flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-violet-600" /> Rettifiche fiscali</div>
            <p className="text-xs text-muted-foreground mt-1">Dal box «trattamento fiscale particolare» in Spese: <b>+ spese capitalizzate</b> (tolte dai costi) e <b>+ quota indeducibile</b> delle spese a deducibilità ridotta.</p>
          </div>
          <div className="rounded-lg border p-3">
            <div className="font-semibold flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-600" /> Interessi e ammortamenti</div>
            <p className="text-xs text-muted-foreground mt-1"><b>− Interessi mutui</b> (dal piano di ammortamento in Mutui) e <b>− ammortamenti deducibili</b> (cespiti in Ammortamenti + beni capitalizzati dalle Spese).</p>
          </div>
          <div className="rounded-lg border p-3">
            <div className="font-semibold flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-rose-600" /> Tasse e cassa</div>
            <p className="text-xs text-muted-foreground mt-1"><b>Tasse</b> = {al}% sull'utile ante imposte (solo se positivo). <b>Utile netto</b> = ante imposte − tasse. <b>Cassa effettiva</b> = (Ricavi − Costi) − interessi − quota capitale mutui − tasse.</p>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground italic mt-3">Valori gestionali indicativi: le imposte effettive vanno confermate con il commercialista.</p>
      </div>

      <div className="gr-card p-0 overflow-x-auto" data-testid="tasse-months-table">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b bg-secondary/30">
              <th className="py-2.5 px-3">Mese</th>
              <th className="py-2.5 px-3 text-right">Ricavi − Costi</th>
              <th className="py-2.5 px-3 text-right">Rettifiche fisc.</th>
              <th className="py-2.5 px-3 text-right">Interessi mutui</th>
              <th className="py-2.5 px-3 text-right">Quota capitale</th>
              <th className="py-2.5 px-3 text-right">Ammortamenti</th>
              <th className="py-2.5 px-3 text-right">Ante imposte</th>
              <th className="py-2.5 px-3 text-right">Tasse ({al}%)</th>
              <th className="py-2.5 px-3 text-right">Utile netto</th>
              <th className="py-2.5 px-3 text-right">Cassa effettiva</th>
            </tr>
          </thead>
          <tbody>
            {righe.map((r) => (
              <tr key={r.m} onClick={() => r.haDati && setDetailMk(r.mk)} className={`border-b last:border-0 ${r.haDati ? "cursor-pointer hover:bg-secondary/40" : "opacity-45"}`} data-testid={`tasse-row-${r.m + 1}`}>
                <td className="py-2.5 px-3 font-semibold">{r.label}</td>
                <td className={`py-2.5 px-3 text-right tabnum ${r.utileRC < 0 ? "text-rose-600" : ""}`}>{r.haDati ? fmtEur(r.utileRC) : "—"}</td>
                <td className="py-2.5 px-3 text-right tabnum text-violet-700">{r.rettifiche ? `+ ${fmtEur(r.rettifiche)}` : "—"}</td>
                <td className="py-2.5 px-3 text-right tabnum text-muted-foreground">{r.interessi ? `− ${fmtEur(r.interessi)}` : "—"}</td>
                <td className="py-2.5 px-3 text-right tabnum text-muted-foreground">{r.capitale ? fmtEur(r.capitale) : "—"}</td>
                <td className="py-2.5 px-3 text-right tabnum text-muted-foreground">{r.amm ? `− ${fmtEur(r.amm)}` : "—"}</td>
                <td className={`py-2.5 px-3 text-right tabnum ${r.anteImposte < 0 ? "text-rose-600" : ""}`}>{r.haDati ? fmtEur(r.anteImposte) : "—"}</td>
                <td className={`py-2.5 px-3 text-right tabnum font-bold ${r.tasse > 0 ? "text-rose-700" : "text-muted-foreground"}`}>{fmtEur(r.tasse)}</td>
                <td className={`py-2.5 px-3 text-right tabnum font-semibold ${r.utileNetto < 0 ? "text-rose-600" : "text-primary"}`}>{r.haDati ? fmtEur(r.utileNetto) : "—"}</td>
                <td className={`py-2.5 px-3 text-right tabnum font-bold ${r.cassa < 0 ? "text-rose-600" : "text-blue-700"}`}>{r.haDati ? fmtEur(r.cassa) : "—"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 font-bold bg-secondary/20">
              <td className="py-2.5 px-3">Totale {anno}</td>
              <td className="py-2.5 px-3 text-right tabnum">{fmtEur(sum((r) => r.utileRC))}</td>
              <td className="py-2.5 px-3 text-right tabnum text-violet-700">+ {fmtEur(sum((r) => r.rettifiche))}</td>
              <td className="py-2.5 px-3 text-right tabnum">− {fmtEur(sum((r) => r.interessi))}</td>
              <td className="py-2.5 px-3 text-right tabnum">{fmtEur(sum((r) => r.capitale))}</td>
              <td className="py-2.5 px-3 text-right tabnum">− {fmtEur(sum((r) => r.amm))}</td>
              <td className="py-2.5 px-3 text-right tabnum">{fmtEur(sum((r) => r.anteImposte))}</td>
              <td className="py-2.5 px-3 text-right tabnum text-rose-700">{fmtEur(totTasse)}</td>
              <td className="py-2.5 px-3 text-right tabnum text-primary">{fmtEur(totUtileNetto)}</td>
              <td className="py-2.5 px-3 text-right tabnum text-blue-700">{fmtEur(totCassa)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="rounded-lg border border-violet-200 bg-violet-50/50 px-4 py-3 text-xs text-muted-foreground flex items-start gap-2" data-testid="tasse-nota-fiscale">
        <Scale size={15} className="text-violet-700 mt-0.5 shrink-0" />
        <span>Le <b>rettifiche fiscali</b> (spese capitalizzate e quota indeducibile) si impostano nel modulo <b>Spese</b>, nel box "Spese con trattamento fiscale particolare". Il modulo Ricavi − Costi resta a costi pieni; qui in Tasse vengono applicate le correzioni fiscali.</span>
      </div>
    </div>
  );
}
